import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs";
import * as moment from 'moment';
import 'moment/locale/it';
import {BaseComponent} from "../base/base.component";
import {CategoryService} from "../../services/category/category.service";
import {CompanyService} from "../../services/company/company.service";
import {AthleteService} from "../../services/athlete/athlete.service";
import {ConvocationService} from "../../services/convocation/convocation.service";
import {ConvocationRowItem} from "../dashboard/dashboard-mister/mister-convocation-shared";
import {Category} from "../../models/category";
import {AthleteSeason} from "../../models/athleteSeason";
import {CompanyDocument} from "../../models/CompanyDocument";
import {
    computeEnrollmentStats,
    enrollmentAmount,
    enrollmentDisplayStatus,
    EnrollmentStats,
    EnrollmentStatus,
    enrollmentStatusIcon,
    enrollmentStatusLabel,
    ENROLLMENT_STATUSES,
    formatEuro,
    hasActiveMembership,
    membershipExpiration,
    percent,
} from "../companies-subscriptions/enrollment";

type StatusFilter = 'all' | EnrollmentStatus | 'noMembership';
type PageTab = 'athletes' | 'convocations';
type ConvocationPeriod = 'upcoming' | 'past';

@Component({
    selector: 'app-subscriptions-category',
    templateUrl: './subscriptions-category.component.html',
    styleUrls: ['./subscriptions-category.component.scss']
})
export class SubscriptionsCategoryComponent extends BaseComponent implements OnInit, OnDestroy {

    companyId = localStorage.getItem('wisper_companyId') ?? '';

    loading = true;
    loadError = false;
    category?: Category;
    parentName = '';
    siblings: Category[] = [];
    enrollments: AthleteSeason[] = [];
    stats: EnrollmentStats = computeEnrollmentStats([]);
    primaryDocumentExists = false;

    search = '';
    statusFilter: StatusFilter = 'all';
    tab: PageTab = 'athletes';

    convocations: ConvocationRowItem[] = [];
    loadingConvocations = true;
    convocationsError = false;
    convocationPeriod: ConvocationPeriod = 'upcoming';
    expandedConvocationId: string | null = null;

    readonly statuses = ENROLLMENT_STATUSES;
    readonly skeletonRows = [1, 2, 3, 4, 5, 6];
    readonly formatEuro = formatEuro;
    readonly percent = percent;
    readonly displayStatus = enrollmentDisplayStatus;
    readonly statusLabel = enrollmentStatusLabel;
    readonly statusIcon = enrollmentStatusIcon;
    readonly amount = enrollmentAmount;
    readonly hasMembership = hasActiveMembership;

    private routeSub?: Subscription;

    constructor(
        public route: ActivatedRoute,
        public injectorService: Injector,
        public categoryService: CategoryService,
        private companyService: CompanyService,
        private athleteService: AthleteService,
        private convocationService: ConvocationService,
    ) {
        super(injectorService);
    }

    ngOnInit() {
        this.loadPrimaryDocumentFlag();
        if (this.route.snapshot.queryParamMap.get('tab') === 'convocations') this.tab = 'convocations';
        this.routeSub = this.route.paramMap.subscribe(params => this.load(params.get('categoryId') ?? ''));
    }

    ngOnDestroy() {
        this.routeSub?.unsubscribe();
    }

    async load(categoryId: string) {
        this.loading = true;
        this.loadError = false;
        this.search = '';
        this.statusFilter = 'all';
        this.expandedConvocationId = null;
        this.loadConvocations(categoryId);

        const [rawCategory, enrollments] = await Promise.all([
            this.categoryService.getCategoryById(categoryId),
            this.athleteService.getEnrollments({categoryId}),
        ]);
        // Ignore a stale response when the user switched category meanwhile.
        if (this.route.snapshot.paramMap.get('categoryId') !== categoryId) return;

        this.category = rawCategory?.id ? new Category(rawCategory) : undefined;
        this.loadError = !enrollments || !this.category;
        this.enrollments = (enrollments ?? [])
            .sort((a, b) => `${a.athlete?.lastName} ${a.athlete?.firstName}`.localeCompare(`${b.athlete?.lastName} ${b.athlete?.firstName}`, 'it'));
        this.stats = computeEnrollmentStats(this.enrollments);

        if (this.category) await this.loadSiblings(this.category);
        this.loading = false;
    }

    get filteredEnrollments(): AthleteSeason[] {
        const query = this.search.trim().toLowerCase();
        return this.enrollments.filter(enrollment => {
            if (this.statusFilter === 'noMembership' && hasActiveMembership(enrollment)) return false;
            if (this.statusFilter !== 'all' && this.statusFilter !== 'noMembership' && enrollment.status !== this.statusFilter) return false;
            if (!query) return true;
            const athlete = enrollment.athlete;
            return [athlete?.firstName, athlete?.lastName, athlete?.fiscalCode, athlete?.email, athlete?.phone]
                .some(value => value?.toLowerCase().includes(query));
        });
    }

    filterCount(filter: StatusFilter): number {
        if (filter === 'all') return this.stats.total;
        if (filter === 'noMembership') return this.stats.withoutMembership;
        return this.stats[filter];
    }

    statusClasses(status?: string): string {
        switch (status) {
            case 'paid': return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
            case 'verifyingPayment': return 'bg-amber-50 text-amber-800 ring-amber-200';
            case 'partialPayment': return 'bg-orange-50 text-orange-800 ring-orange-200';
            case 'pendingPayment': return 'bg-red-50 text-red-700 ring-red-200';
            default: return 'bg-slate-50 text-slate-600 ring-slate-200';
        }
    }

    membershipText(enrollment: AthleteSeason): string {
        const expiration = membershipExpiration(enrollment);
        if (!expiration) return 'Mai tesserato';
        return expiration.isAfter(moment())
            ? `Scade il ${expiration.format('DD/MM/YYYY')}`
            : `Scaduto il ${expiration.format('DD/MM/YYYY')}`;
    }

    documentState(enrollment: AthleteSeason): {status: string, text: string} {
        return this.utils.remaningDays(enrollment.athlete?.primaryDocExpirationDate ?? '');
    }

    documentClasses(status: string): string {
        switch (status) {
            case 'success': return 'text-emerald-700';
            case 'warning': return 'text-amber-700';
            case 'danger': return 'text-red-600';
            default: return 'text-slate-400';
        }
    }

    initials(enrollment: AthleteSeason): string {
        const athlete = enrollment.athlete;
        return `${athlete?.firstName?.[0] ?? ''}${athlete?.lastName?.[0] ?? ''}`.toUpperCase() || '?';
    }

    exportCsv() {
        const header = ['Cognome', 'Nome', 'Codice fiscale', 'Email', 'Telefono', 'Stato pagamento', 'Quota', 'Tesseramento'];
        const rows = this.filteredEnrollments.map(enrollment => [
            enrollment.athlete?.lastName, enrollment.athlete?.firstName, enrollment.athlete?.fiscalCode,
            enrollment.athlete?.email, enrollment.athlete?.phone, enrollmentStatusLabel(enrollmentDisplayStatus(enrollment)),
            enrollmentAmount(enrollment).toFixed(2).replace('.', ','), this.membershipText(enrollment),
        ]);
        // Semicolons + BOM so Excel (Italian locale) opens it with the right columns and accents.
        const csv = [header, ...rows]
            .map(row => row.map(value => `"${(value ?? '').toString().replace(/"/g, '""')}"`).join(';'))
            .join('\r\n');
        const url = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}));
        const link = document.createElement('a');
        link.href = url;
        link.download = `iscritti-${(this.category?.name ?? 'categoria').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    // ---- Convocations ----

    async loadConvocations(categoryId: string) {
        this.loadingConvocations = true;
        this.convocationsError = false;
        const response = await this.convocationService.listConvocations({categoryId});
        if (this.route.snapshot.paramMap.get('categoryId') !== categoryId) return;

        this.convocationsError = !response?.success;
        this.convocations = response?.data?.rows ?? [];
        // Land on the past ones when nothing is scheduled.
        this.convocationPeriod = this.upcomingConvocations.length || !this.pastConvocations.length ? 'upcoming' : 'past';
        this.loadingConvocations = false;
    }

    get upcomingConvocations(): ConvocationRowItem[] {
        const now = moment();
        return this.convocations
            .filter(convocation => moment(convocation.date).isSameOrAfter(now))
            .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    }

    get pastConvocations(): ConvocationRowItem[] {
        const now = moment();
        return this.convocations
            .filter(convocation => moment(convocation.date).isBefore(now))
            .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
    }

    get visibleConvocations(): ConvocationRowItem[] {
        return this.convocationPeriod === 'upcoming' ? this.upcomingConvocations : this.pastConvocations;
    }

    get nextConvocation(): ConvocationRowItem | undefined {
        return this.upcomingConvocations.find(convocation => convocation.status !== 'Bozza');
    }

    convocationStatusClasses(status: string): string {
        switch (status) {
            case 'Pubblicata': return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
            case 'Bozza': return 'bg-amber-50 text-amber-800 ring-amber-200';
            case 'Chiusa': return 'bg-slate-100 text-slate-600 ring-slate-200';
            default: return 'bg-slate-50 text-slate-600 ring-slate-200';
        }
    }

    convocationDay(convocation: ConvocationRowItem): string {
        return moment(convocation.date).format('DD');
    }

    convocationMonth(convocation: ConvocationRowItem): string {
        return moment(convocation.date).locale('it').format('MMM').replace('.', '');
    }

    convocationWhen(convocation: ConvocationRowItem): string {
        const date = moment(convocation.date).locale('it');
        return `${date.format('dddd D MMMM')} · ore ${date.format('HH:mm')}`;
    }

    convocationRelative(convocation: ConvocationRowItem): string {
        return moment(convocation.date).locale('it').fromNow();
    }

    convocatedAthletes(convocation: ConvocationRowItem): {id: string, name: string, enrolled: boolean}[] {
        return (convocation.users ?? [])
            .map(id => {
                const athlete = this.enrollments.find(enrollment => enrollment.athleteId === id)?.athlete;
                return athlete
                    ? {id, name: `${athlete.lastName} ${athlete.firstName}`, enrolled: true}
                    : {id, name: 'Atleta non più iscritto', enrolled: false};
            })
            .sort((a, b) => Number(b.enrolled) - Number(a.enrolled) || a.name.localeCompare(b.name, 'it'));
    }

    toggleConvocation(convocation: ConvocationRowItem) {
        this.expandedConvocationId = this.expandedConvocationId === convocation.id ? null : convocation.id;
    }

    convocationFormOpen = false;

    openNewConvocation() {
        this.convocationFormOpen = true;
    }

    async onConvocationCreated(convocation: ConvocationRowItem) {
        this.convocationFormOpen = false;
        await this.loadConvocations(this.category?.id ?? '');
        this.convocationPeriod = moment(convocation.date).isBefore(moment()) ? 'past' : 'upcoming';
        this.expandedConvocationId = convocation.id;
    }

    trackByConvocation(_: number, convocation: ConvocationRowItem) {
        return convocation.id;
    }

    goToCategory(categoryId: string) {
        if (categoryId && categoryId !== this.category?.id) {
            this.navigate.navigate(['/companies/subscriptions/category', categoryId]);
        }
    }

    trackByAthlete(_: number, enrollment: AthleteSeason) {
        return enrollment.athleteId;
    }

    private async loadSiblings(category: Category) {
        const categories = (await this.categoryService.getCategories(this.companyId, false, {seasonId: category.seasonId}))
            .map(item => new Category(item));
        const parent = categories.find(item => item.id === category.parentId);
        this.parentName = parent?.name ?? '';
        this.siblings = categories
            .filter(item => item.parentId)
            .map(item => {
                const itemParent = categories.find(candidate => candidate.id === item.parentId);
                return new Category({...item, name: itemParent ? `${itemParent.name} · ${item.name}` : item.name});
            })
            .sort((a, b) => a.name.localeCompare(b.name, 'it', {numeric: true}));
    }

    private async loadPrimaryDocumentFlag() {
        try {
            const documents: CompanyDocument[] = await this.companyService.getCompanyDocuments(this.companyId);
            this.primaryDocumentExists = documents.some(document => document.primaryDocument === 1);
        } catch (error) {
            console.error('Error fetching company documents:', error);
        }
    }
}
